import { Box, SelectChangeEvent, Table, TableBody, TableContainer } from "@mui/material";
import Pagination, { PAGE_OPTIONS } from "components/Pagination";
import ScreenLoader from "components/ScreenLoader";
import { MSG_OPERATION_FAILED, MSG_OPERATION_SUCCESSFUL } from "constants/index";
import { useQueryParams } from "hooks/useQueryParams";
import { TPagination } from "modules/api/type";
import { deleteCircuitEntry, getCircuitList } from "modules/circuit/circuitServices";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import CircuitCreateEditModal from "../CircuitCreateEditModal";
import { TableHeader, TableItem } from "./TableComponents";

const CircuitsTab = ({ searchValue }) => {
  const [loading, setLoading] = useState(false);
  const [showEditCircuitModal, setShowEditCircuitModal] = useState(false);
  const [entryToEditId, setEntryToEditId] = useState<number>();
  const [params, setParams, defaultParams] = useQueryParams({
    limit: 10,
    page: 1,
    search: "",
    sort_by: "NAME",
    sort_order: "ASC",
  });
  const [pagination, setPagination] = useState<TPagination>({
    current_page: 0,
    data_from_offset: 0,
    data_to_offset: 0,
    per_page: PAGE_OPTIONS[0],
    total_pages: 0,
    total_records: 0,
  });
  const [tableEntries, setTableEntries] = useState<any[]>([]);

  useEffect(() => {
    let temp = { ...params };
    if (searchValue) {
      setParams("search", searchValue);
      temp = { ...params, search: searchValue };
    } else {
      setParams("search", "");
      temp = { ...params, search: "" };
    }
    fetchData(temp);
  }, [searchValue]);

  const fetchData = useCallback(
    async (parameters) => {
      if (loading) return;
      setLoading(true);
      let result = await getCircuitList(parameters);
      setLoading(false);
      if (result.success) {
        setTableEntries(result.data);
        setPagination(result.pagination);
      }
    },
    [setTableEntries, setPagination, loading, setLoading]
  );

  //#region event handlers
  const handlePaginationOptionChange = (event: SelectChangeEvent<any>) => {
    let temp = { ...params, limit: event.target.value, page: 1 };
    setParams("limit", event.target.value);
    fetchData(temp);
  };

  const handlePageChange = (page: number) => {
    let temp = { ...params, page };
    setParams("page", page);
    fetchData(temp);
  };

  const handleSortChange = (sort_by: string, sort_order: string) => {
    let temp = { ...params, page: 1, sort_by, sort_order };
    setParams("page", 1);
    setParams("sort_by", sort_by);
    setParams("sort_order", sort_order);
    fetchData(temp);
  };

  const handleEditCallback = (id: number) => {
    setEntryToEditId(id);
    setShowEditCircuitModal(true);
  };

  const handleCloseModal = (isRefreshNeeded = false) => {
    setShowEditCircuitModal(false);
    if (typeof isRefreshNeeded == "boolean" && isRefreshNeeded) {
      setLoading(true);
      window.location.reload();
    }
  };

  const handleDeleteCallback = (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Are you sure you want to delete this entry?`)) {
      setLoading(true);
      deleteCircuitEntry(id)
        .then((r) => {
          toast(MSG_OPERATION_SUCCESSFUL, { type: "success" });
          window.location.reload();
        })
        .catch((e) => {
          console.log(e);
          toast(MSG_OPERATION_FAILED, { type: "error" });
        });
    }
  };
  //#endregion event handlers

  //console.log(`Table Entries: ${JSON.stringify(tableEntries)}`);

  return (
    <>
      <div>
        <TableContainer sx={{ paddingBottom: "20px" }} className="staff-accounts-table">
          <Table stickyHeader aria-label="sticky table">
            <TableHeader sortingCallback={handleSortChange} pageParams={params} />
            <TableBody>
              {!loading &&
                tableEntries.map((e, index) => (
                  <TableItem
                    data={e}
                    index={index}
                    key={`tableitem${index}`}
                    editCallback={handleEditCallback}
                    deleteCallback={handleDeleteCallback}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && (
          <Box mt="30px">
            <ScreenLoader isScreenLoader={true} />
          </Box>
        )}

        <Pagination
          {...pagination}
          onPageChange={handlePageChange}
          pageDropdownProps={{
            defaultValue: pagination.per_page,
            onChange: handlePaginationOptionChange,
            value: pagination.per_page,
          }}
        />
      </div>

      <CircuitCreateEditModal
        open={showEditCircuitModal}
        onClose={handleCloseModal}
        entryId={entryToEditId}
      />
    </>
  );
};

export default CircuitsTab;
